import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useLocalizedPath } from '@/hooks/useLocalizedPath';
import { ArrowLeftRight, Clock, Map, Calendar, Clock3, ArrowRight, ShieldCheck, Wallet, CheckCircle } from 'lucide-react';
import AddressAutocomplete from './AddressAutocomplete';
import { getLabels, TripType } from './bookingShared';
import type { SelectedPlace } from '@/hooks/useGoogleMaps';

const HomeBookingWidget = () => {
  const { lang } = useLanguage();
  const { localePath } = useLocalizedPath();
  const navigate = useNavigate();
  const L = getLabels(lang);

  const [trip, setTrip] = useState<TripType>('oneway');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [fromPlace, setFromPlace] = useState<SelectedPlace | undefined>();
  const [toPlace, setToPlace] = useState<SelectedPlace | undefined>();
  const [date, setDate] = useState('');
  const [time, setTime] = useState('12:00');
  const [hours, setHours] = useState(3);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const params = new URLSearchParams({
      trip,
      from,
      to: trip === 'hourly' ? '' : to,
      date,
      time,
      hours: String(hours),
    });
    if (fromPlace?.placeId) {
      params.set('fromId', fromPlace.placeId);
      if (fromPlace.isSalzburg) params.set('fromSz', '1');
    }
    if (trip === 'oneway' && toPlace?.placeId) {
      params.set('toId', toPlace.placeId);
      if (toPlace.isSalzburg) params.set('toSz', '1');
    }
    navigate(`${localePath('/booking')}?${params.toString()}`);
  };


  const tabs: { id: TripType; label: string; icon: React.ReactNode }[] = [
    { id: 'oneway', label: L.oneway, icon: <ArrowLeftRight className="w-4 h-4" /> },
    { id: 'hourly', label: L.hourly, icon: <Clock className="w-4 h-4" /> },
    { id: 'daytrip', label: L.daytrip, icon: <Map className="w-4 h-4" /> },
  ];

  return (
    <div className="w-full max-w-md bg-card/95 backdrop-blur-md rounded-2xl shadow-2xl border border-border p-5 md:p-6">
      {/* Tabs */}
      <div className="flex bg-muted rounded-xl p-1 mb-5">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setTrip(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-2 px-2 rounded-lg text-xs font-semibold transition-all ${
              trip === tab.id ? 'bg-background text-primary shadow-sm' : 'text-muted-foreground hover:text-foreground'
            }`}
          >
            {tab.icon}
            <span className="hidden sm:inline">{tab.label}</span>
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="space-y-3">
        <AddressAutocomplete
          value={from}
          onChange={(v, p) => { setFrom(v); if (p) setFromPlace(p); else setFromPlace(undefined); }}
          label={trip === 'hourly' ? L.pickup : L.from}
          placeholder={L.placeholder}
        />

        {trip === 'oneway' && (
          <AddressAutocomplete
            value={to}
            onChange={(v, p) => { setTo(v); if (p) setToPlace(p); else setToPlace(undefined); }}
            label={L.to}
            placeholder={L.placeholder}
          />
        )}


        {trip === 'hourly' && (
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">{L.hours}</label>
            <select
              value={hours}
              onChange={(e) => setHours(Number(e.target.value))}
              className="w-full px-3 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 outline-none"
            >
              {[2,3,4,5,6,8,10,12].map(h => <option key={h} value={h}>{h} h</option>)}
            </select>
          </div>
        )}

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">{L.date}</label>
            <div className="relative">
              <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                required
                className="w-full pl-10 pr-2 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 outline-none"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-foreground mb-1.5">{L.time}</label>
            <div className="relative">
              <Clock3 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <input
                type="time"
                value={time}
                onChange={(e) => setTime(e.target.value)}
                required
                className="w-full pl-10 pr-2 py-3 rounded-xl border border-border bg-background text-foreground text-sm focus:ring-2 focus:ring-primary/50 outline-none"
              />
            </div>
          </div>
        </div>

        <button
          type="submit"
          className="w-full inline-flex items-center justify-center gap-2 mt-2 px-6 py-3.5 rounded-xl bg-gradient-gold text-primary-foreground font-bold shadow-gold hover:scale-[1.02] transition-transform"
        >
          {L.getPrice} <ArrowRight className="w-4 h-4" />
        </button>
      </form>

      <div className="flex items-center justify-around gap-2 pt-4 mt-4 border-t border-border text-[10px] text-muted-foreground">
        <span className="flex items-center gap-1"><Wallet className="w-3 h-3 text-primary" />{L.trustPay}</span>
        <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-primary" />{L.trustCancel}</span>
        <span className="flex items-center gap-1"><CheckCircle className="w-3 h-3 text-primary" />{L.trustConfirm}</span>
      </div>
    </div>
  );
};

export default HomeBookingWidget;
